import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, BarChart3, Plus, DollarSign, TrendingUp,
  Eye, Target, CreditCard, Receipt, LogOut, Loader2, Clock,
  CheckCircle, AlertCircle, Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdvertiserDashboard() {
  const navigate = useNavigate();
  const [advertiser, setAdvertiser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/me`, { withCredentials: true });
      setAdvertiser(response.data);
      fetchDashboard();
    } catch (error) {
      navigate("/advertiser/login");
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/advertiser-portal/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/advertiser/login");
  };

  const statusColors = {
    draft: "bg-slate-500/20 text-slate-400",
    pending_approval: "bg-amber-500/20 text-amber-400",
    approved: "bg-blue-500/20 text-blue-400",
    active: "bg-emerald-500/20 text-emerald-400",
    paused: "bg-orange-500/20 text-orange-400",
    completed: "bg-purple-500/20 text-purple-400",
    rejected: "bg-red-500/20 text-red-400"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="advertiser-dashboard">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ParkWise
                </span>
                <span className="hidden sm:inline text-slate-400 text-sm ml-2">Advertiser Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400 hidden sm:block">
                {dashboard?.company_name}
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2">
            <Link to="/advertiser" className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium whitespace-nowrap">
              <LayoutDashboard className="w-4 h-4 inline mr-2" />Dashboard
            </Link>
            <Link to="/advertiser/campaigns" className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white text-sm font-medium whitespace-nowrap">
              <BarChart3 className="w-4 h-4 inline mr-2" />Campaigns
            </Link>
            <Link to="/advertiser/billing" className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white text-sm font-medium whitespace-nowrap">
              <CreditCard className="w-4 h-4 inline mr-2" />Billing
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome + Quick Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome back, {advertiser?.contact_name?.split(' ')[0]}
            </h1>
            <p className="text-slate-400 text-sm mt-1">Here's how your campaigns are performing</p>
          </div>
          <Button 
            onClick={() => navigate("/advertiser/campaigns/new")} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-campaign-cta"
          >
            <Plus className="w-4 h-4 mr-2" />New Campaign
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Active Campaigns</p>
                  <p className="text-2xl font-bold text-white mt-1">{dashboard?.stats?.active_campaigns || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Impressions</p>
                  <p className="text-2xl font-bold text-white mt-1">{(dashboard?.stats?.total_impressions || 0).toLocaleString()}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Visit Scans</p>
                  <p className="text-2xl font-bold text-white mt-1">{(dashboard?.stats?.total_visits || 0).toLocaleString()}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Total Spend</p>
                  <p className="text-2xl font-bold text-white mt-1">£{(dashboard?.stats?.total_spend || 0).toFixed(2)}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Account Balance + Pending */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-600/20 to-emerald-600/10 border-blue-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-blue-300">Account Balance</h3>
                <DollarSign className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-3xl font-bold text-white">£{(dashboard?.account_balance || 0).toFixed(2)}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4 border-blue-500 text-blue-400"
                onClick={() => navigate("/advertiser/billing")}
              >
                Add Funds
              </Button>
            </CardContent>
          </Card>
          
          {dashboard?.stats?.pending_approval > 0 && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-amber-300">Pending Approval</h3>
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-3xl font-bold text-white">{dashboard.stats.pending_approval}</p>
                <p className="text-sm text-amber-300 mt-2">Campaigns awaiting review</p>
              </CardContent>
            </Card>
          )}
          
          {dashboard?.outstanding_balance > 0 && (
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-red-300">Outstanding</h3>
                  <AlertCircle className="w-5 h-5 text-red-400" />
                </div>
                <p className="text-3xl font-bold text-white">£{dashboard.outstanding_balance.toFixed(2)}</p>
                <Button 
                  size="sm" 
                  className="mt-4 bg-red-600 hover:bg-red-700"
                  onClick={() => navigate("/advertiser/billing")}
                >
                  Pay Now
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Campaigns */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Recent Campaigns</CardTitle>
            <Link to="/advertiser/campaigns" className="text-blue-400 text-sm hover:underline">
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_campaigns?.length > 0 ? (
              <div className="space-y-4">
                {dashboard.recent_campaigns.map((campaign) => (
                  <div 
                    key={campaign.campaign_id} 
                    className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-900"
                    onClick={() => navigate(`/advertiser/campaigns/${campaign.campaign_id}`)}
                  >
                    <div>
                      <p className="font-medium text-white">{campaign.name}</p>
                      <p className="text-sm text-slate-400">
                        {campaign.schedule?.start_date} - {campaign.schedule?.end_date}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm text-slate-400">{campaign.metrics?.impressions_delivered || 0} impressions</p>
                      </div>
                      <Badge className={statusColors[campaign.status]}>
                        {campaign.status?.replace("_", " ")}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No campaigns yet</p>
                <Button 
                  className="mt-4 bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate("/advertiser/campaigns/new")}
                >
                  Create Your First Campaign
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Start Guide */}
        {dashboard?.stats?.total_campaigns === 0 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Quick Start Guide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-900/50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold mb-3">1</div>
                  <h4 className="font-medium text-white mb-1">Create Campaign</h4>
                  <p className="text-sm text-slate-400">Choose your target audience and campaign type</p>
                </div>
                <div className="p-4 bg-slate-900/50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold mb-3">2</div>
                  <h4 className="font-medium text-white mb-1">Upload Creative</h4>
                  <p className="text-sm text-slate-400">Add images or videos for your campaign</p>
                </div>
                <div className="p-4 bg-slate-900/50 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold mb-3">3</div>
                  <h4 className="font-medium text-white mb-1">Submit for Review</h4>
                  <p className="text-sm text-slate-400">Our team will review and activate your campaign</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
